import React, { useState } from 'react';
import { ServiceType } from '../types';
import { Beaker, ShieldCheck, Package, Factory, Truck } from 'lucide-react';

const Process: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ServiceType>(ServiceType.LAUNCH);

  const launchSteps = [
    { 
        icon: <Beaker />, 
        title: "Strategy & Formulation", 
        time: "Phase 1", 
        desc: "We align on vision, select proven formulas or create custom blends, source premium ingredients, and optimize flavor and texture." 
    },
    { 
        icon: <ShieldCheck />, 
        title: "Compliance & Licensing", 
        time: "Phase 2", 
        desc: "We handle FSSAI license applications, lab testing, nutritional panels, and legal review so you launch with regulatory confidence." 
    },
    { 
        icon: <Package />, 
        title: "Branding & Packaging", 
        time: "Phase 3", 
        desc: "Professional brand identity, label design that builds trust, packaging selection, and compliance verification for print-ready artwork." 
    },
    { 
        icon: <Factory />, 
        title: "Manufacturing & QC", 
        time: "Phase 4", 
        desc: "Batch production in GMP/FSSAI certified facilities. In-process testing, COA verification, and finished goods inspection." 
    },
    { 
        icon: <Truck />, 
        title: "Launch & Fulfillment", 
        time: "Phase 5", 
        desc: "Website setup support, marketplace listing (Amazon/Flipkart), inventory management, and nationwide fulfillment setup." 
    },
  ];

  const contractSteps = [
    { 
        icon: <Beaker />, 
        title: "Requirement Assessment", 
        time: "Step 1", 
        desc: "We review your volume needs, existing formulas, and timeline expectations to provide a transparent quote with zero hidden fees." 
    },
    { 
        icon: <ShieldCheck />, 
        title: "Compliance Review", 
        time: "Step 2", 
        desc: "Ingredient legality verification, label accuracy checks, and testing requirement audits to prevent costly mistakes." 
    },
    { 
        icon: <Factory />, 
        title: "Manufacturing", 
        time: "Step 3", 
        desc: "Production in GMP facilities with batch-level quality testing, real-time updates, and complete COA documentation." 
    },
    { 
        icon: <Truck />, 
        title: "Delivery & Partnership", 
        time: "Step 4", 
        desc: "Finished goods delivered to your warehouse. Easy reorder systems, new SKU development support, and scaling infrastructure." 
    },
  ];

  const activeSteps = activeTab === ServiceType.LAUNCH ? launchSteps : contractSteps;

  return (
    <section id="process" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
          <p className="text-xl text-gray-600">From concept to customer, or from chaos to consistency.</p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-16">
          <div className="bg-gray-100 p-1 rounded-xl inline-flex flex-wrap justify-center">
            <button
              onClick={() => setActiveTab(ServiceType.LAUNCH)}
              className={`px-6 py-3 rounded-lg text-sm font-bold transition-all ${
                activeTab === ServiceType.LAUNCH
                  ? 'bg-orange-500 text-black shadow-md'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Brand Launch (5 Phases)
            </button>
            <button
              onClick={() => setActiveTab(ServiceType.CONTRACT)}
              className={`px-6 py-3 rounded-lg text-sm font-bold transition-all ${
                activeTab === ServiceType.CONTRACT
                  ? 'bg-black text-white shadow-md'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Contract Mfg (4 Steps)
            </button>
          </div>
        </div>

        {/* Steps Display */}
        <div className="relative">
          {/* Connecting Line (Desktop) */}
          <div className="hidden lg:block absolute top-[60px] left-0 w-full h-1 bg-gray-100 -z-10"></div>

          <div className={`grid gap-8 ${activeTab === ServiceType.LAUNCH ? 'md:grid-cols-2 lg:grid-cols-5' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
            {activeSteps.map((step, index) => (
              <div key={index} className="relative group h-full">
                <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col items-center text-center">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 shadow-lg ${
                    activeTab === ServiceType.LAUNCH ? 'bg-orange-500 text-black' : 'bg-black text-white'
                  }`}>
                    {React.cloneElement(step.icon as React.ReactElement<any>, { size: 24 })}
                  </div>
                  
                  <div className="mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-600 border border-gray-200 px-2 py-1 rounded-md">
                      {step.time}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;